import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AudioPlayer } from '@/components/AudioPlayer';
import { Breadcrumb } from '@/components/Breadcrumb';
import { FileList } from '@/components/FileList';
import { FolderTree } from '@/components/FolderTree';
import { SearchBar, type SearchMode } from '@/components/SearchBar';
import { TextViewer } from '@/components/TextViewer';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { usePlayer } from '@/contexts/PlayerContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ru } from '@/i18n/ru';
import { ApiClientError } from '@/lib/api';
import { saveAiSearchHistory, type AiHistoryEntry } from '@/lib/aiSearchHistory';
import { libraryApi, searchApi, type SearchResponse } from '@/lib/endpoints';
import { parentPathOf, type LibraryItem } from '@/lib/format';

export function BrowsePage() {
  const auth = useAuth();
  const player = usePlayer();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const path = params.get('path') ?? '';
  const mode = (params.get('mode') === 'ai' ? 'ai' : 'files') as SearchMode;
  const urlQ = params.get('q') ?? '';

  const [query, setQuery] = useState(urlQ);
  const [aiSubmitted, setAiSubmitted] = useState(mode === 'ai' ? urlQ : '');
  /** Bumps on every Search click so the same query re-fetches from the API. */
  const [aiRunId, setAiRunId] = useState(0);
  /** Snapshot shown only when user picks an entry from history (no API). */
  const [aiHistoryView, setAiHistoryView] = useState<SearchResponse | null>(null);
  const debouncedFilesQ = useDebouncedValue(mode === 'files' ? query : '', 200);

  const [textFile, setTextFile] = useState<{ id: string; filename: string } | null>(null);
  const [highlightFileId, setHighlightFileId] = useState<string | null>(null);

  useEffect(() => {
    setQuery(urlQ);
    if (mode === 'ai' && urlQ) setAiSubmitted(urlQ);
  }, [urlQ, mode]);

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const navigatePath = useCallback(
    (nextPath: string, highlightId?: string | null) => {
      updateParams({
        path: nextPath || null,
        q: null,
        mode: null,
      });
      setQuery('');
      setAiSubmitted('');
      setAiHistoryView(null);
      setHighlightFileId(highlightId ?? null);
    },
    [updateParams],
  );

  const isSearching =
    mode === 'files' ? debouncedFilesQ.trim().length > 0 : aiSubmitted.trim().length > 0;
  const activeSearchQ = mode === 'files' ? debouncedFilesQ.trim() : aiSubmitted.trim();

  const filesQuery = useInfiniteQuery({
    queryKey: ['files', path],
    queryFn: ({ pageParam }) => libraryApi.files(path, pageParam as string | undefined, 100),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !isSearching,
  });

  const filesSearchQuery = useQuery({
    queryKey: ['search', 'files', debouncedFilesQ],
    queryFn: () => searchApi.files(debouncedFilesQ.trim()),
    enabled: mode === 'files' && debouncedFilesQ.trim().length > 0,
    staleTime: 30_000,
  });

  const aiSearchQuery = useQuery({
    queryKey: ['search', 'ai', aiSubmitted, aiRunId],
    queryFn: async () => {
      const q = aiSubmitted.trim();
      const response = await searchApi.ai(q);
      saveAiSearchHistory(q, response);
      return response;
    },
    enabled: mode === 'ai' && aiSubmitted.trim().length > 0 && aiHistoryView === null,
    staleTime: 0,
    gcTime: 1000 * 60 * 30,
  });

  const folderItems: LibraryItem[] = useMemo(
    () => filesQuery.data?.pages.flatMap((p) => p.entries) ?? [],
    [filesQuery.data],
  );

  const searchItems: LibraryItem[] = useMemo(() => {
    if (mode === 'files') return filesSearchQuery.data?.results ?? [];
    if (aiHistoryView) return aiHistoryView.results;
    return aiSearchQuery.data?.results ?? [];
  }, [mode, filesSearchQuery.data, aiHistoryView, aiSearchQuery.data]);

  const listItems = isSearching ? searchItems : folderItems;

  const listLoading = isSearching
    ? mode === 'files'
      ? filesSearchQuery.isFetching
      : aiHistoryView
        ? false
        : aiSearchQuery.isFetching
    : filesQuery.isLoading;

  const listError = (() => {
    if (aiHistoryView) return null;
    const err = isSearching
      ? mode === 'files'
        ? filesSearchQuery.error
        : aiSearchQuery.error
      : filesQuery.error;
    if (!err) return null;
    if (err instanceof ApiClientError) return err.message;
    return ru.requestFailed;
  })();

  const onModeChange = (next: SearchMode) => {
    updateParams({
      mode: next === 'files' ? null : 'ai',
      q: query.trim() || null,
    });
    if (next === 'ai') {
      /* wait for explicit submit */
    } else if (query.trim()) {
      setAiSubmitted('');
      setAiHistoryView(null);
    }
  };

  const onQueryChange = (q: string) => {
    setQuery(q);
    if (mode === 'files') {
      updateParams({ q: q.trim() || null, mode: null });
    }
  };

  const onSubmitAi = () => {
    const q = query.trim();
    if (!q) return;
    setAiHistoryView(null);
    setAiRunId((n) => n + 1);
    setAiSubmitted(q);
    updateParams({ q, mode: 'ai' });
  };

  const onSelectAiHistory = (entry: AiHistoryEntry) => {
    setAiHistoryView(entry.response);
    setQuery(entry.q);
    setAiSubmitted(entry.q);
    updateParams({ q: entry.q, mode: 'ai' });
  };

  const onRevealInFolder = (item: LibraryItem) => {
    const parent =
      'parentPath' in item && typeof item.parentPath === 'string'
        ? item.parentPath
        : parentPathOf(item.relativePath);
    navigatePath(parent, item.id);
  };

  const onLogout = async () => {
    await auth.logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const typing =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable;

      if (e.key === 'Escape') {
        if (textFile) {
          setTextFile(null);
          return;
        }
      }

      if (typing) return;

      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        player.togglePlay();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        player.seekBy(-5);
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        player.seekBy(5);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [player, textFile]);

  useEffect(() => {
    if (aiSearchQuery.isError && aiSearchQuery.error instanceof ApiClientError) {
      toast.error(aiSearchQuery.error.message);
    }
  }, [aiSearchQuery.isError, aiSearchQuery.error]);

  // Clear highlight after a short moment so it doesn't stick forever
  useEffect(() => {
    if (!highlightFileId || isSearching) return;
    const t = window.setTimeout(() => setHighlightFileId(null), 8000);
    return () => window.clearTimeout(t);
  }, [highlightFileId, isSearching]);

  return (
    <div className="flex h-full flex-col" data-testid="browse-page">
      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-[var(--sidebar-w)] shrink-0 md:block">
          <FolderTree currentPath={path} onNavigate={(p) => navigatePath(p)} />
        </aside>

        <main className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-[var(--topbar-h)] shrink-0 items-center gap-3 border-b border-border bg-surface/40 px-3">
            <div className="hidden min-w-0 max-w-[240px] lg:block">
              <Breadcrumb path={isSearching ? '' : path} onNavigate={(p) => navigatePath(p)} />
              {isSearching ? (
                <p className="truncate text-[11px] text-text-muted">
                  {mode === 'ai' ? ru.aiSearch : ru.fileSearch} · {activeSearchQ}
                </p>
              ) : null}
            </div>
            <SearchBar
              query={query}
              mode={mode}
              loading={mode === 'ai' && aiSearchQuery.isFetching}
              onQueryChange={onQueryChange}
              onModeChange={onModeChange}
              onSubmitAi={onSubmitAi}
              onSelectAiHistory={onSelectAiHistory}
            />
            <div className="flex shrink-0 items-center gap-2">
              <span className="hidden text-[11px] text-text-muted sm:inline">
                {auth.username}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void onLogout()}
                data-testid="logout-button"
              >
                <LogOut className="h-3.5 w-3.5" />
                {ru.logout}
              </Button>
            </div>
          </header>

          <div className="relative min-h-0 flex-1">
            {mode === 'ai' && aiSearchQuery.isFetching ? (
              <div className="absolute inset-x-0 top-0 z-10 border-b border-border bg-surface px-3 py-1.5 text-[11px] text-text-muted">
                {ru.searchingSounds}
              </div>
            ) : null}
            <div className="absolute inset-0 flex flex-col">
              <FileList
                items={listItems}
                highlightQuery={isSearching && mode === 'files' ? activeSearchQ : undefined}
                loading={listLoading && listItems.length === 0}
                error={listError}
                onRetry={() => {
                  if (isSearching) {
                    if (mode === 'files') void filesSearchQuery.refetch();
                    else void aiSearchQuery.refetch();
                  } else {
                    void filesQuery.refetch();
                  }
                }}
                onOpenText={(item) => setTextFile({ id: item.id, filename: item.filename })}
                onRevealInFolder={isSearching ? onRevealInFolder : undefined}
                emptyTitle={isSearching ? ru.noMatches : ru.emptyFolder}
                emptyDescription={isSearching ? ru.tryDifferentQuery : ru.selectFolder}
                hasMore={!isSearching && Boolean(filesQuery.hasNextPage)}
                loadingMore={filesQuery.isFetchingNextPage}
                onLoadMore={() => void filesQuery.fetchNextPage()}
                highlightFileId={!isSearching ? highlightFileId : null}
              />
            </div>
          </div>
        </main>
      </div>

      <AudioPlayer />

      <TextViewer
        open={Boolean(textFile)}
        fileId={textFile?.id ?? null}
        filename={textFile?.filename}
        onOpenChange={(open) => {
          if (!open) setTextFile(null);
        }}
      />
    </div>
  );
}
